// Collapse by groups (query general.collapse), shown as the Diversify box of the query: the best
// items of the query, at most N per value of a field. builder-query writes OpenSearch collapse into
// the query; general.collapse present means on, absent means off.

// OpenSearch collapses on keyword and NumberFieldType only: scaled_float and date are refused,
// unsigned_long is accepted (checked on OpenSearch 2.19.1). Same list as control-center-api.
const numericDbTypes = new Set([
  "long",
  "integer",
  "short",
  "byte",
  "double",
  "float",
  "half_float",
  "unsigned_long"
]);

// OpenSearch collapses only on a keyword or numeric field, not nested, with one value per
// document: a document with more values makes the whole search fail. control-center-api marks
// the catalog metadata with multiValued from the data; a field without the flag is not offered.
export function isCollapsibleField(meta) {
  return (
    meta?.multiValued === false &&
    !meta.dbNestedField &&
    (meta.dbType === "keyword" || numericDbTypes.has(meta.dbType))
  );
}

// filtro for SelectMeta: keep it a stable function, SelectMeta regroups when it changes. The
// selection is undefined until the metadata table is read (passed through, as filterDefault
// does), and useSelectMem returns the row itself, not an array, when one row matches.
export const collapsibleFields = (list) => {
  if (!list) return list;
  return (Array.isArray(list) ? list : [list]).filter(isCollapsibleField);
};

// The values with the Diversify field set: a field turns collapse on (the other collapse settings
// kept), an empty one removes the key (off)
export function withCollapseField(values, field) {
  const general = values.general || {};

  if (field) return { ...values, general: { ...general, collapse: { ...general.collapse, field } } };

  const { collapse, ...rest } = general;
  return { ...values, general: rest };
}

// The values with Items per value (perGroup) set. 1 is the same as absent: it removes perGroup and
// the distribution, which only orders the items above 1. Any other input is kept as typed, the
// distribution with it: 0, 1.5 or an emptied input ("") are drafts the validation refuses.
export function withCollapsePerGroup(values, perGroup) {
  const general = values.general || {};
  const { perGroup: _perGroup, distribution, ...collapse } = general.collapse || {};

  if (perGroup === 1) return { ...values, general: { ...general, collapse } };

  return {
    ...values,
    general: {
      ...general,
      collapse: { ...collapse, perGroup, ...(distribution !== undefined && { distribution }) }
    }
  };
}

// The values with the Order (distribution) of the items above 1 per value
export function withCollapseDistribution(values, distribution) {
  const general = values.general || {};
  return { ...values, general: { ...general, collapse: { ...general.collapse, distribution } } };
}

// The orders of the items above 1 per value: they change only the order, not which items are kept.
// The example is three values (A, B, C) with two items each, all kept. roundRobin is the default.
export const DISTRIBUTIONS = [
  { value: "roundRobin", label: "Alternate", title: "Alternate groups", example: "A1 B1 C1 A2 B2 C2" },
  { value: "grouped", label: "Together", title: "Keep groups together", example: "A1 A2 B1 B2 C1 C2" },
  { value: "sorted", label: "Ranking", title: "Keep ranking order", example: "A1 A2 B1 C1 B2 C2" }
];

// The order of a collapse: Alternate when no distribution is saved
export const distributionOf = (collapse) =>
  DISTRIBUTIONS.find((d) => d.value === collapse?.distribution) ?? DISTRIBUTIONS[0];

// "<N> per value · <order>" for the box header, or undefined at 1 per value
export function diversifySummary(collapse) {
  if (!(collapse?.perGroup > 1)) return undefined;
  return `${collapse.perGroup} per value · ${distributionOf(collapse).label}`;
}

// The validation message for general.collapse, or undefined. rows: the rows of the metadata table
// (catalog, id, multiValued), as an array or an iterator (the table Map values, read without
// cloning: validation runs at every keystroke); undefined before the metadata are loaded.
export function collapseError(values, rows) {
  const collapse = values?.general?.collapse;
  if (!collapse) return undefined;

  const field = typeof collapse.field === "string" ? collapse.field.trim() : "";
  if (!field) return "Choose the field to diversify by";

  if (
    collapse.perGroup !== undefined &&
    (!Number.isInteger(collapse.perGroup) || collapse.perGroup < 1)
  )
    return "Items per value must be a whole number from 1 up";

  // Any row of the field marked multi-valued: the catalog configuration may list the same id in a
  // row without the flag, before the rows read from the index
  let multiValued = false;
  for (const m of rows ?? []) {
    if (m.catalog === values.contentType && m.id === field && m.multiValued === true) {
      multiValued = true;
      break;
    }
  }
  if (multiValued)
    return `Field ${field} now has more than one value per document: Diversify cannot use it. Choose another field and check the other queries that use it`;

  return undefined;
}

// Under this many values a Diversify field gets a warning (the metadata know the count for the
// fields under 10K values)
export const FEW_VALUES = 100;

// The warning for a Diversify field with few values, or undefined. With few groups the block
// returns at most perGroup items per value (plus perGroup for the items without the field), and
// OpenSearch cannot skip documents early when the query asks more groups than exist: a broad query
// evaluates them all (bench: 30 ms with 3 groups, 640 ms with 80 on a 2-value field).
export function fewValuesWarning(meta, perGroup = 1, threshold = FEW_VALUES) {
  const count = meta?.valuesLength;
  if (typeof count !== "number" || count >= threshold) return undefined;

  const n = Number.isInteger(perGroup) && perGroup > 1 ? perGroup : 1;
  const each = n === 1 ? "one" : n;

  return `This field has ${count} value${count === 1 ? "" : "s"}: the block returns at most ${
    (count + 1) * n
  } items (${each} per value, plus ${each} for the items without the field), and broad queries can get slow.`;
}
